import { createContext, useMemo, useContext } from "react";

import { useWeeklyContext } from "../global/WeeklyContext";

import { getWholeWeeksBetween, today } from "../../utils/datetime";

/**
 * Page context for Hours Log week-distance helpers.
 */
export const HoursLogContext = createContext(null);

/**
 * Reads HoursLogContext. Throws if used outside an HoursLog provider.
 *
 * @returns {Object} Hours log context value.
 */
export function useHoursLogContext() {
  const context = useContext(HoursLogContext);
  if (!context) {
    throw new Error(
      "useHoursLogContext must be used within an HoursLogProvider"
    );
  }
  return context;
}

/**
 * Builds Hours Log context: whole weeks between the selected week start and today.
 *
 * @returns {{wholeWeeks: number}} Hours log page value.
 */
export function useHoursLogCtx() {
  const { weekStart } = useWeeklyContext();

  const wholeWeeks = useMemo(
    () => getWholeWeeksBetween(weekStart, today()),
    [weekStart]
  );

  const contextValue = useMemo(
    // Memo so if another component calls it doesn't re-run context unless changed
    () => ({
      wholeWeeks,
    }),
    [wholeWeeks]
  );

  return {
    ...contextValue,
  };
}

export default useHoursLogCtx; // Export the hook as default for backward compatibility
